"use client";

import { Collection } from "react-aria-components/Collection";
import type { Selection } from "react-aria-components";
import { toast } from "@heroui/react";
import * as React from "react";
import {
  IconFileText as FileText,
  IconFile as File,
  IconPhoto as Image,
  IconMusic as Music,
  IconVideo as Video,
  IconDotsVertical as MoreVertical,
  IconFolderPlus as FolderPlus,
  IconPencil as Edit,
  IconTrash as Trash2,
  IconPlus as Plus,
} from "@tabler/icons-react";
import {
  AlertDialog,
  Button,
  cn,
  Dropdown,
  Label,
  ListBox,
} from "@heroui/react";
import { FileTree, Segment, useFileTree } from "@heroui-pro/react";
import { RacDialogTriggerAnchor } from "@/components/RacDialogTriggerAnchor";
import { trpc } from "@/lib/trpc";
import { buildTree } from "@hs/shared";
import type { TreeSelectNode } from "@/components/custom";
import { CategoryEditDialog, type CategoryEditMode, type CategoryFlatItemForTree } from "./CategoryEditDialog";

export type AssetsScope = "my" | "shared";
export type MediaTypeFilter = "all" | "document" | "image" | "audio" | "video";

const MEDIA_TYPE_OPTIONS: {
  key: MediaTypeFilter;
  label: string;
  icon: React.ComponentType<{ className?: string; stroke?: number }>;
}[] = [
  { key: "all", label: "全部", icon: File },
  { key: "image", label: "图片", icon: Image },
  { key: "audio", label: "音频", icon: Music },
  { key: "video", label: "视频", icon: Video },
  { key: "document", label: "文档", icon: FileText },
];

type CategoryItem = { id: string; name?: string | null; parentId?: string | null; order?: number };

interface CategorySidebarNode extends TreeSelectNode {
  category: CategoryItem;
}

function findInCategoryTree(nodes: CategorySidebarNode[], id: string): CategorySidebarNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const hit = findInCategoryTree(n.children as CategorySidebarNode[], id);
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * 分类行操作菜单（添加下级分类、编辑、删除）
 */
function CategoryActionsMenu({
  category,
  onAddChild,
  onEdit,
  onDelete,
}: {
  category: CategoryItem;
  onAddChild?: (category: CategoryItem) => void;
  onEdit?: (category: CategoryItem) => void;
  onDelete?: (category: CategoryItem) => void;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div
      className={cn(
        "absolute end-0 top-1/2 z-10 flex -translate-y-1/2 items-center pe-0.5",
        "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
        menuOpen && "opacity-100"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown isOpen={menuOpen} onOpenChange={setMenuOpen}>
        <Button
          isIconOnly
          aria-label="操作"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground !h-6 !min-h-6 !w-6 !min-w-6 !p-0"
        >
          <MoreVertical className="size-3.5" stroke={1.5} />
        </Button>
        <Dropdown.Popover className="min-w-[160px]" placement="bottom end">
          <Dropdown.Menu>
            <Dropdown.Item id="add-child" textValue="添加下级分类" onAction={() => onAddChild?.(category)}>
              <Label className="flex items-center gap-2">
                <FolderPlus className="size-4" stroke={1.5} />
                添加下级分类
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="edit" textValue="编辑" onAction={() => onEdit?.(category)}>
              <Label className="flex items-center gap-2">
                <Edit className="size-4" stroke={1.5} />
                编辑
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="delete" textValue="删除" variant="danger" onAction={() => onDelete?.(category)}>
              <Label className="flex items-center gap-2">
                <Trash2 className="size-4" stroke={1.5} />
                删除
              </Label>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}

function renderCategoryTreeItem(
  item: CategorySidebarNode,
  ctx: {
    onAddChild: (c: CategoryItem) => void;
    onEdit: (c: CategoryItem) => void;
    onDelete: (c: CategoryItem) => void;
  }
) {
  const hasKids = !!(item.children && item.children.length > 0);
  const titleNode = (
    <span className="relative flex min-w-0 flex-1 items-center pe-7">
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <CategoryActionsMenu
        category={item.category}
        onAddChild={ctx.onAddChild}
        onEdit={ctx.onEdit}
        onDelete={ctx.onDelete}
      />
    </span>
  );

  return (
    <FileTree.Item className="group" dragIcon={false} id={item.id} textValue={item.label} title={titleNode}>
      {hasKids ? (
        <Collection items={item.children as CategorySidebarNode[]}>
          {(child) => renderCategoryTreeItem(child as CategorySidebarNode, ctx)}
        </Collection>
      ) : null}
    </FileTree.Item>
  );
}

export interface AssetsSidebarProps {
  scope: AssetsScope;
  mediaType: MediaTypeFilter;
  selectedCategoryId: string | null;
  onScopeChange: (scope: AssetsScope) => void;
  onMediaTypeChange: (type: MediaTypeFilter) => void;
  onCategorySelect: (categoryId: string | null, categoryName?: string | null) => void;
}

export function AssetsSidebar({
  scope,
  mediaType,
  selectedCategoryId,
  onScopeChange,
  onMediaTypeChange,
  onCategorySelect,
}: AssetsSidebarProps) {
  const categoryScope = scope === "my" ? "MINE" : "SPACE";
  const categoryTreeInput = React.useMemo(() => ({ type: "MEDIA" as const, scope: categoryScope }), [categoryScope]);
  const { data: categoryFlatList = [], isLoading: categoryTreeLoading, refetch: refetchCategoryTree } =
    trpc.categories.getCategoryTree.useQuery(categoryTreeInput as never, { staleTime: 0 });

  const handleCategoryDialogSuccess = React.useCallback(async () => {
    await refetchCategoryTree();
  }, [refetchCategoryTree]);

  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [categoryDialogMode, setCategoryDialogMode] = React.useState<CategoryEditMode>("create");
  const [editingCategory, setEditingCategory] = React.useState<CategoryItem | null>(null);
  const [parentCategoryForAddChild, setParentCategoryForAddChild] = React.useState<CategoryItem | null>(null);

  const handleEditCategory = React.useCallback((category: CategoryItem) => {
    setEditingCategory(category);
    setParentCategoryForAddChild(null);
    setCategoryDialogMode("update");
    setCategoryDialogOpen(true);
  }, []);

  const handleAddCategory = React.useCallback(() => {
    setEditingCategory(null);
    setParentCategoryForAddChild(null);
    setCategoryDialogMode("create");
    setCategoryDialogOpen(true);
  }, []);

  const handleAddChildCategory = React.useCallback((category: CategoryItem) => {
    setEditingCategory(null);
    setParentCategoryForAddChild(category);
    setCategoryDialogMode("add-child");
    setCategoryDialogOpen(true);
  }, []);

  const [categoryToDelete, setCategoryToDelete] = React.useState<CategoryItem | null>(null);
  const deleteCategoryMutation = trpc.categories.delete.useMutation({
    onSuccess: (_data, variables) => {
      toast.success("分类已删除");
      setCategoryToDelete(null);
      if (selectedCategoryId === variables.id) {
        onCategorySelect(null, null);
      }
      refetchCategoryTree();
    },
  });

  const handleDeleteCategory = React.useCallback((category: CategoryItem) => {
    setCategoryToDelete(category);
  }, []);

  const handleConfirmDeleteCategory = React.useCallback(() => {
    if (categoryToDelete?.id) {
      deleteCategoryMutation.mutate({ id: categoryToDelete.id });
    }
  }, [categoryToDelete, deleteCategoryMutation]);

  const categoryCtx = React.useMemo(
    () => ({
      onAddChild: handleAddChildCategory,
      onEdit: handleEditCategory,
      onDelete: handleDeleteCategory,
    }),
    [handleAddChildCategory, handleEditCategory, handleDeleteCategory]
  );

  const categoryFileItems: CategorySidebarNode[] = React.useMemo(() => {
    const flat = (categoryFlatList as CategoryItem[]).filter((c) => c.id != null);
    if (flat.length === 0) return [];
    const tree = buildTree(flat, {
      parentField: "parentId",
      childrenField: "children",
      sortBy: "order",
      sortOrder: "asc",
    }) as { id: string; name?: string | null; parentId?: string | null; children?: unknown[] }[];
    const convert = (nodes: typeof tree): CategorySidebarNode[] =>
      nodes.map((node) => ({
        id: node.id,
        label: node.name ?? "",
        category: { id: node.id, name: node.name, parentId: node.parentId },
        children: node.children?.length ? convert(node.children as typeof tree) : undefined,
      }));
    return convert(tree);
  }, [categoryFlatList]);

  const { expandableKeys } = useFileTree({ items: categoryFileItems });

  const selectedKeys = React.useMemo<Selection>(
    () => (selectedCategoryId ? new Set([selectedCategoryId]) : new Set()),
    [selectedCategoryId]
  );

  const handleTreeSelection = React.useCallback(
    (keys: Selection) => {
      if (keys === "all" || keys.size === 0) {
        onCategorySelect(null, null);
        return;
      }
      const id = [...keys][0] as string;
      const node = findInCategoryTree(categoryFileItems, id);
      if (!node) return;
      onCategorySelect(node.id, node.label ?? null);
    },
    [categoryFileItems, onCategorySelect]
  );

  const handleScopeSegmentChange = React.useCallback(
    (key: React.Key) => {
      const k = String(key) as AssetsScope;
      if (k === "my" || k === "shared") {
        onScopeChange(k);
        onCategorySelect(null, null);
      }
    },
    [onScopeChange, onCategorySelect]
  );

  const mediaTypeSelectedKeys = React.useMemo<Selection>(() => {
    if (selectedCategoryId != null) return new Set();
    return new Set([mediaType]);
  }, [mediaType, selectedCategoryId]);

  const handleMediaTypeSelectionChange = React.useCallback(
    (keys: Selection) => {
      if (keys === "all" || keys.size === 0) return;
      const id = [...keys][0] as string;
      const next = id as MediaTypeFilter;
      if (MEDIA_TYPE_OPTIONS.some((o) => o.key === next)) {
        onMediaTypeChange(next);
        onCategorySelect(null, null);
      }
    },
    [onMediaTypeChange, onCategorySelect]
  );

  return (
    <>
      <div className="bg-sidebar flex h-full w-64 shrink-0 flex-col border-r border-[var(--border)]">
        <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-4">
          <Segment
            className="min-w-0 w-full"
            selectedKey={scope}
            onSelectionChange={handleScopeSegmentChange}
          >
            <Segment.Item id="my">
              <Segment.Separator />
              我的
            </Segment.Item>
            <Segment.Item id="shared">
              <Segment.Separator />
              共享
            </Segment.Item>
          </Segment>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
          <div className="shrink-0">
            <div className="text-muted-foreground mb-2 px-2 py-1.5 text-xs font-medium">按类型查看</div>
            <ListBox
              aria-label="媒体类型"
              selectionMode="single"
              selectedKeys={mediaTypeSelectedKeys}
              onSelectionChange={handleMediaTypeSelectionChange}
            >
              {MEDIA_TYPE_OPTIONS.map((item) => {
                const Icon = item.icon;
                return (
                  <ListBox.Item
                    key={item.key}
                    id={item.key}
                    textValue={item.label}
                    className={cn(
                      "w-full gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                      "hover:bg-surface-secondary",
                      "aria-selected:bg-surface-secondary",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    )}
                  >
                    <Icon className="size-4 shrink-0" stroke={1.5} />
                    <Label className="truncate">{item.label}</Label>
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                );
              })}
            </ListBox>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-4">
            <div className="mb-2 flex shrink-0 items-center justify-between gap-1 px-2 py-1.5">
              <span className="text-muted-foreground text-xs font-medium">按分类查看</span>
              <Button
                isIconOnly
                aria-label="新建分类"
                size="sm"
                variant="ghost"
                className="!h-6 !min-h-6 !w-6 !min-w-6 !p-0"
                onPress={handleAddCategory}
              >
                <Plus className="size-4" stroke={1.5} />
              </Button>
            </div>
            {categoryTreeLoading ? (
              <div className="text-muted-foreground shrink-0 py-4 text-center text-sm">加载中…</div>
            ) : categoryFileItems.length === 0 ? (
              <div className="text-muted-foreground shrink-0 py-4 text-center text-sm">暂无分类</div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <FileTree
                  aria-label="资产分类"
                  className="[&_[data-slot=file-tree-icon]]:hidden [&_[data-slot=file-tree-item]]:mb-1"
                  defaultExpandedKeys={expandableKeys}
                  items={categoryFileItems}
                  selectedKeys={selectedKeys}
                  selectionBehavior="replace"
                  selectionMode="single"
                  showGuideLines="hover"
                  size="md"
                  onSelectionChange={handleTreeSelection}
                >
                  {(item) => renderCategoryTreeItem(item as CategorySidebarNode, categoryCtx)}
                </FileTree>
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog isOpen={!!categoryToDelete} onOpenChange={(open) => !open && setCategoryToDelete(null)}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除「{categoryToDelete?.name ?? "该"}」分类吗？删除后不会删除分类内的文件。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button slot="close" variant="secondary" onPress={() => setCategoryToDelete(null)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteCategoryMutation.isPending}
                  onPress={handleConfirmDeleteCategory}
                >
                  {deleteCategoryMutation.isPending ? "删除中…" : "确定删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <CategoryEditDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        mode={categoryDialogMode}
        scope={scope}
        categoryData={editingCategory ?? undefined}
        parentCategory={parentCategoryForAddChild ?? undefined}
        categoryFlatListFromParent={categoryFlatList as CategoryFlatItemForTree[]}
        onSuccess={handleCategoryDialogSuccess}
      />
    </>
  );
}
